import type { GifType } from '../gifSearches/types';
import type {
  FileReference,
  FileUploadJobReference,
  ImageFileReference,
  SharePointFileLinkReference,
  SharePointVideoLinkReference,
  SharePointWebLinkReference,
  VideoFileReference,
  WebImageReference,
  WebLinkReference,
  WebVideoReference,
} from '../types';
import type { WebLink } from '../webLinks/types';

export type AttachmentMenuItemState = 'Enabled' | 'Disabled' | 'Hidden';

export type AttachmentsAllowedStatus =
  | 'AllAllowed'
  | 'DestinationNotSelected'
  | 'DisabledForClient'
  | 'DisabledForNetwork'
  | 'GroupNotSelected'
  | 'HiddenForPostOnBehalfOfUser'
  | 'IsEditing'
  | 'MaxAttachments'
  | 'MediaOnlyAllowed'
  | 'NoUploadInPM'
  | 'NetworkSettingsUnavailable'
  | 'ThreadLevelGuest'
  | 'UserLevel'
  | 'ViewerCommunityRelationshipUnknown'
  | 'DisallowedForPublisherType'
  | 'DisallowedForPrivateReplyPublisherType';

export interface PublisherFileReference extends FileReference {
  readonly editorPlaceholderId?: string;
  readonly wasAttachedViaUpload: boolean;
  readonly isHeaderImage?: boolean;
}

export interface PublisherImageFileReference extends ImageFileReference {
  readonly editorPlaceholderId?: string;
  readonly fileUploadJobId?: string;
  readonly wasAttachedViaUpload: boolean;
  readonly isHeaderImage?: boolean;
}

export interface PublisherVideoFileReference extends VideoFileReference {
  readonly editorPlaceholderId?: string;
  readonly fileUploadJobId?: string;
  readonly wasAttachedViaUpload: boolean;
}

export interface PublisherWebLinkReference extends WebLinkReference {
  readonly attachedViaMRU: boolean;
  readonly isAttachedViaFilePicker: boolean;
}

export interface PublisherSharePointFileLinkReference extends SharePointFileLinkReference {
  readonly isAttachedViaFilePicker: boolean;
}

export interface PublisherSharePointVideoLinkReference extends SharePointVideoLinkReference {
  readonly isAttachedViaFilePicker: boolean;
}

export interface PublisherSharePointWebLinkReference extends SharePointWebLinkReference {
  readonly isAttachedViaFilePicker: boolean;
}

export interface ResolvingWebLinkReference {
  readonly id: WebLink['url'];
  readonly schema: 'resolvingWebLinks';
  readonly gifType?: GifType;
}

export interface ResolvingWebLinkInlineReference {
  readonly id: WebLink['url'];
  readonly schema: 'resolvingWebLinksInline';
}

export const isPublisherFileAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherFileReference | PublisherImageFileReference | PublisherVideoFileReference =>
  reference.schema === 'files' || reference.schema === 'imageFiles' || reference.schema === 'videoFiles';

export const isPublisherSharePointAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherSharePointFileLinkReference | PublisherSharePointVideoLinkReference =>
  reference.schema === 'sharePointFileLinks' || reference.schema === 'sharePointVideoLinks';

export const isPublisherWebLinkReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebLinkReference => reference.schema === 'webLinks';

export const isPublisherSharePointWebLinkReference = (
  reference: PublisherAttachmentReference
): reference is PublisherSharePointWebLinkReference => reference.schema === 'sharePointWebLinks';

export const isPublisherWebImageReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebImageReference => reference.schema === 'webImages';

export const isPublisherGifReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebImageReference => reference.schema === 'webImages' && reference.gifType !== undefined;

export const isPublisherWebVideoReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebVideoReference => reference.schema === 'webVideos';

export const isPublisherOdspRecentAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebLinkReference => isPublisherWebLinkReference(reference) && reference.attachedViaMRU;

export const isPublisherOdspFilePickerAttachmentReference = (
  reference: PublisherAttachmentReference
): reference is PublisherWebLinkReference | PublisherSharePointFileLinkReference =>
  (isPublisherWebLinkReference(reference) || isPublisherSharePointAttachmentReference(reference)) &&
  reference.isAttachedViaFilePicker;

export interface PublisherWebImageReference extends WebImageReference {
  readonly gifType?: GifType;
  readonly tags?: string[];
}

export type PublisherWebVideoReference = WebVideoReference;

export type PublisherAttachmentReference =
  | PublisherFileReference
  | PublisherImageFileReference
  | PublisherSharePointFileLinkReference
  | PublisherSharePointVideoLinkReference
  | PublisherSharePointWebLinkReference
  | PublisherVideoFileReference
  | PublisherWebImageReference
  | PublisherWebLinkReference
  | PublisherWebVideoReference
  | FileUploadJobReference
  | ResolvingWebLinkReference
  | ResolvingWebLinkInlineReference;

export type PublisherAttachments = PublisherAttachmentReference[];
